import { registerQuery } from '@adornis/baseql/metadata/register.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import {
  ApolloAppliedFilters,
  ApolloLeadPreview,
  ApolloLeadSearchInput,
  ApolloLeadSearchResult,
} from '../baseql/apollo-lead-search.js';
import { resolveIndustryTagIds } from './apollo-industry-tags.js';
import { type IcpMapping, mapIcpToApolloFilters } from './icp-to-apollo.js';

interface ApolloOrganizationSearchResult {
  name?: string | null;
  industry?: string | null;
}

interface ApolloPersonSearchResult {
  id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  last_name_obfuscated?: string | null;
  name?: string | null;
  title?: string | null;
  linkedin_url?: string | null;
  last_refreshed_at?: string | null;
  has_email?: boolean | null;
  has_direct_phone?: string | null;
  organization?: ApolloOrganizationSearchResult | null;
}

interface ApolloPeopleSearchResponse {
  total_entries?: number | null;
  people?: ApolloPersonSearchResult[] | null;
}

export interface ApolloOrgSearchOrganization {
  id?: string | null;
  name?: string | null;
  industry?: string | null;
  industry_tag_id?: string | null;
  estimated_num_employees?: number | null;
}

interface ApolloOrgSearchResponse {
  organizations?: ApolloOrgSearchOrganization[] | null;
  pagination?: { total_entries?: number | null } | null;
}

const splitList = (value?: string | null): string[] =>
  (value ?? '')
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

const employeeRange = (value?: string | null): string | null => {
  const normalized = (value ?? '').trim();
  if (!normalized) return null;
  if (normalized.endsWith('+')) return `${normalized.replace('+', '').trim()},1000000`;
  const match = normalized.match(/^(\d+)\s*-\s*(\d+)$/);
  if (!match) return null;
  return `${match[1]},${match[2]}`;
};

const appendList = (params: URLSearchParams, key: string, values: string[]): void => {
  values.forEach(value => {
    params.append(key, value);
  });
};

// Apollo's location filters only accept cities, US states or country names.
// Expand the friendly UI labels we expose into the actual countries Apollo
// understands so picking "Europe" / "DACH" actually returns hits.
const LOCATION_ALIASES: Record<string, string[]> = {
  europe: [
    'austria',
    'belgium',
    'bulgaria',
    'croatia',
    'czech republic',
    'denmark',
    'estonia',
    'finland',
    'france',
    'germany',
    'greece',
    'hungary',
    'ireland',
    'italy',
    'latvia',
    'lithuania',
    'luxembourg',
    'netherlands',
    'norway',
    'poland',
    'portugal',
    'romania',
    'slovakia',
    'slovenia',
    'spain',
    'sweden',
    'switzerland',
    'united kingdom',
  ],
  dach: ['germany', 'austria', 'switzerland'],
  'san francisco bay area': ['san francisco', 'oakland', 'san jose', 'palo alto', 'mountain view'],
  // Abbreviation-suffixed UI labels + common shorthands.
  'united kingdom (uk)': ['united kingdom'],
  'united states (usa)': ['united states'],
  uk: ['united kingdom'],
  usa: ['united states'],
  // UK constituent countries — Apollo knows them as "<region>, United Kingdom".
  england: ['England, United Kingdom'],
  scotland: ['Scotland, United Kingdom'],
  wales: ['Wales, United Kingdom'],
  'northern ireland': ['Northern Ireland, United Kingdom'],
};

const expandLocations = (values: string[]): string[] => {
  const out: string[] = [];
  for (const raw of values) {
    const key = raw.trim().toLowerCase();
    const expansion = LOCATION_ALIASES[key];
    if (expansion) out.push(...expansion);
    else if (raw.trim()) out.push(raw.trim());
  }
  return out;
};

const dedupeStrings = (values: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const key = v.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  return out;
};

/**
 * Reads an optional field off a BaseQL entity. If the field was omitted from
 * the remote request, the getter throws ("not requested from remote"); we treat
 * that as "no value provided" and return undefined.
 */
const readOptionalField = <T>(read: () => T): T | undefined => {
  try {
    return read();
  } catch {
    return undefined;
  }
};

/**
 * The company-side filters of a search — used to decide whether a verified
 * organization pre-search is worthwhile and to build its params.
 */
export interface Firmographics {
  orgLocations: string[];
  /**
   * Person locations, used as the org-search location fallback: a person-type
   * ICP ("teachers in Germany") often carries the country only on the PERSON
   * side — without the fallback the org stage fetched companies worldwide and
   * the people stage then found near-zero Germany-based people inside them.
   */
  personLocations: string[];
  /**
   * Company names the operator gave explicitly (typed, or classified out of an
   * uploaded file). When non-empty this SELECTS the org universe on its own —
   * see buildOrgSearchParams and verifyOrganization.
   */
  organizationNames: string[];
  industryTagIds: string[];
  industryKeywords: string[];
  employeeRanges: string[];
  revenueMin: number | null;
  revenueMax: number | null;
}

const buildApolloParams = (
  input: ApolloLeadSearchInput,
  mapping: IcpMapping,
): { params: URLSearchParams; applied: ApolloAppliedFilters; firmographics: Firmographics } => {
  const params = new URLSearchParams();
  params.set('page', String(input.page ?? 1));
  params.set('per_page', String(input.perPage ?? 5));

  // Titles: union of the user's explicit Job Title field and titles the LLM
  // extracted from the ICP description.
  const titles = dedupeStrings([...splitList(input.jobTitle), ...mapping.personTitles]);
  if (titles.length) {
    appendList(params, 'person_titles[]', titles);
    params.set('include_similar_titles', 'true');
  }

  // Seniorities come from the LLM mapper.
  if (mapping.personSeniorities.length) {
    appendList(params, 'person_seniorities[]', mapping.personSeniorities);
  }

  // Locations: union of the user's explicit form fields and what the LLM
  // extracted from the ICP description. Both go through expandLocations so
  // friendly aliases like "DACH" / "Europe" are expanded to country lists.
  const orgLocations = dedupeStrings([
    ...expandLocations(input.companyLocation ?? []),
    ...expandLocations(mapping.organizationLocations),
  ]);
  appendList(params, 'organization_locations[]', orgLocations);

  // Apollo's person_locations and organization_locations are independent
  // filters — constraining the company to the Netherlands does NOT constrain
  // the person to it (confirmed against the live API: identical "CEOs of
  // Dutch manufacturing companies" search returns 3757 hits without
  // person_locations vs. 2702 with it forced to match). Without an explicit
  // person location, mirror the company location onto the person filter so a
  // company-nationality ICP ("Dutch manufacturing companies") doesn't surface
  // decision-makers who are personally located anywhere else (Basel, India, …).
  const explicitPersonLocations = dedupeStrings([
    ...expandLocations(input.personLocation ?? []),
    ...expandLocations(mapping.personLocations),
  ]);
  const personLocations = explicitPersonLocations.length > 0 ? explicitPersonLocations : orgLocations;
  appendList(params, 'person_locations[]', personLocations);

  // Industries: try to resolve the Company Industry dropdown values + the
  // LLM-extracted industry keywords to Apollo's STRUCTURED industry tag ids.
  // The structured filter matches only a company's classified industry —
  // unlike keyword tags, which fuzzily match anywhere in a company's keyword
  // list (Microsoft carries a "manufacturing" keyword tag…). Only when EVERY
  // label resolves do we use tag ids; a partial split would AND the two filter
  // types together and silently over-constrain OR-intended synonyms. Niche
  // business types ("kitchen studio") keep using keyword tags.
  const industryLabels = dedupeStrings([...(input.companyIndustry ?? []), ...mapping.organizationIndustryKeywords]);
  const resolution = resolveIndustryTagIds(industryLabels);
  const useIndustryTagIds = industryLabels.length > 0 && resolution.unresolved.length === 0;
  if (useIndustryTagIds) {
    appendList(params, 'organization_industry_tag_ids[]', resolution.tagIds);
  } else if (industryLabels.length) {
    // Restrict the fuzzy match to the company's actual keyword tags + name: by
    // default Apollo also matches SEO/social-media descriptions, which lets
    // e.g. an IT consultancy whose website mentions "manufacturing" clients
    // slip through an industry filter.
    appendList(params, 'q_organization_keyword_tags[]', industryLabels);
    appendList(params, 'included_organization_keyword_fields[]', ['tags', 'name']);
  }

  // Employee counts: union of the explicit UI multi-select and ranges the LLM
  // extracted from the ICP description (e.g. "headcount between 100 and 5000").
  const employeeCounts = dedupeStrings([
    ...((input.employeeCount ?? []).map(employeeRange).filter(Boolean) as string[]),
    ...mapping.employeeRanges,
  ]);
  appendList(params, 'organization_num_employees_ranges[]', employeeCounts);

  // Revenue min/max: explicit slider values (in dollars) win; otherwise fall
  // back to a revenue range the LLM extracted from the ICP description. These
  // fields are optional and may be omitted from the request entirely.
  // Accessing an omitted field on a BaseQL entity throws ("not requested from
  // remote"), so read defensively.
  const revenueMin = readOptionalField(() => input.revenueMin) ?? mapping.revenueMinUsd ?? undefined;
  const revenueMax = readOptionalField(() => input.revenueMax) ?? mapping.revenueMaxUsd ?? undefined;
  if (revenueMin != null) params.set('revenue_range[min]', String(revenueMin));
  if (revenueMax != null) params.set('revenue_range[max]', String(revenueMax));

  // Explicit company names. Deliberately NOT unioned with anything the LLM
  // guessed: naming a company is a statement, an interpretation is not, and
  // this list decides which companies the org stage selects.
  // Read defensively: drafts persisted before this field existed carry no value
  // for it, and the entity getter throws rather than returning undefined —
  // the same reason excludeApolloPersonIds is read this way.
  const organizationNames = dedupeStrings(readOptionalField(() => input.organizationNames) ?? []);

  const hasStructuredFilters =
    titles.length > 0 ||
    mapping.personSeniorities.length > 0 ||
    personLocations.length > 0 ||
    orgLocations.length > 0 ||
    organizationNames.length > 0 ||
    industryLabels.length > 0 ||
    employeeCounts.length > 0 ||
    revenueMin != null ||
    revenueMax != null;

  // `q_keywords` is a very broad full-text filter in Apollo and can easily
  // over-constrain otherwise valid structured searches.
  const usedQKeywords = !hasStructuredFilters && mapping.qKeywords ? mapping.qKeywords : '';
  if (usedQKeywords) params.set('q_keywords', usedQKeywords);

  // Snapshot of the effective filters so the UI can show the user how their
  // ICP was interpreted.
  const applied = new ApolloAppliedFilters({
    personTitles: titles,
    personSeniorities: mapping.personSeniorities,
    personLocations,
    organizationLocations: orgLocations,
    organizationNames: organizationNames.length > 0 ? organizationNames : undefined,
    organizationIndustryKeywords: industryLabels,
    employeeRanges: employeeCounts,
    departments: mapping.departments,
    revenueMinUsd: revenueMin ?? undefined,
    revenueMaxUsd: revenueMax ?? undefined,
    qKeywords: usedQKeywords,
    mappingFailed: mapping.mappingFailed ? true : undefined,
  });

  const firmographics: Firmographics = {
    orgLocations,
    organizationNames,
    personLocations: explicitPersonLocations,
    industryTagIds: useIndustryTagIds ? resolution.tagIds : [],
    industryKeywords: useIndustryTagIds ? [] : industryLabels,
    employeeRanges: employeeCounts,
    revenueMin: revenueMin ?? null,
    revenueMax: revenueMax ?? null,
  };

  return { params, applied, firmographics };
};

const assertApolloResponse = (value: unknown): ApolloPeopleSearchResponse => {
  if (!value || typeof value !== 'object') throw new Error('Apollo returned an invalid response');
  return value as ApolloPeopleSearchResponse;
};

const apolloApiKey = (): string => {
  const key = process.env.APOLLO_API_KEY;
  if (!key) throw new Error('APOLLO_API_KEY not configured');
  return key;
};

// Apollo hard-caps per_page at 100 — anything above returns HTTP 422
// {"error":"Per page not supported"} (verified against the live API). The
// campaign wizard lets users request up to 1000 leads, so a single request
// can never carry the whole count; see fetchApolloPeople for the paging loop
// that keeps every request within this limit.
const APOLLO_MAX_PAGE_SIZE = 100;

/**
 * Apollo's error bodies are small JSON objects like {"error":"..."} — surface
 * that message instead of dumping the raw status + body up to the UI.
 */
const apolloErrorMessage = (context: string, status: number, bodyText: string): string => {
  try {
    const parsed = JSON.parse(bodyText) as { error?: unknown; message?: unknown };
    const detail =
      typeof parsed.error === 'string' ? parsed.error : typeof parsed.message === 'string' ? parsed.message : null;
    if (detail) return `${context}: ${detail}`;
  } catch {
    /* body wasn't JSON — fall through to the generic message */
  }
  return `${context} (HTTP ${status})`;
};

const runApolloSearch = async (params: URLSearchParams): Promise<ApolloPeopleSearchResponse> => {
  const url = `https://api.apollo.io/api/v1/mixed_people/api_search?${params.toString()}`;
  console.log('[apollo] POST', url);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Cache-Control': 'no-cache', 'Content-Type': 'application/json', 'x-api-key': apolloApiKey() },
  });
  const bodyText = await response.text();
  if (!response.ok) {
    console.error('[apollo] error', response.status, bodyText);
    throw new Error(apolloErrorMessage('Apollo lead search failed', response.status, bodyText));
  }
  return assertApolloResponse(JSON.parse(bodyText));
};

/**
 * Page through mixed_people/api_search in ≤100-sized requests (Apollo's hard
 * limit) until `desiredCount` leads are collected or Apollo runs out —
 * without this, a campaign asking for e.g. 250 leads would send a single
 * per_page=250 request and Apollo would reject the ENTIRE search.
 *
 * Apollo's ranking isn't stable across requests — verified against the live
 * API, even repeating the SAME page (identical params, back to back) came
 * back only ~70% identical — so consecutive pages routinely echo people an
 * earlier page already returned. Dedupe by person id and keep paging within a
 * bounded number of extra attempts (rather than trusting page N to be
 * disjoint from page N-1) so the result still converges close to
 * `desiredCount` instead of silently returning fewer unique leads.
 */
const fetchApolloPeople = async (
  params: URLSearchParams,
  desiredCount: number,
  startPage: number,
): Promise<ApolloPeopleSearchResponse> => {
  const people: ApolloPersonSearchResult[] = [];
  const seenIds = new Set<string>();
  let totalEntries = 0;
  let page = startPage;
  const maxAttempts = Math.ceil(desiredCount / APOLLO_MAX_PAGE_SIZE) + 5;
  // per_page must stay CONSTANT across the loop: Apollo computes the record
  // window as page*per_page, so shrinking per_page on later pages ("only 50
  // still missing") re-offsets the window into records the previous page
  // already covered — the batch then dedupes away to nothing and a 150-lead
  // request silently returned 100.
  const pageSize = Math.min(APOLLO_MAX_PAGE_SIZE, desiredCount);
  params.set('per_page', String(pageSize));
  for (let attempt = 0; attempt < maxAttempts && people.length < desiredCount; attempt++) {
    params.set('page', String(page));
    const data = await runApolloSearch(params);
    totalEntries = data.total_entries ?? totalEntries;
    const batch = data.people ?? [];
    for (const person of batch) {
      if (person.id) {
        if (seenIds.has(person.id)) continue;
        seenIds.add(person.id);
      }
      people.push(person);
    }
    if (batch.length < pageSize) break; // Apollo has nothing further to give.
    page++;
  }
  return { total_entries: totalEntries, people: people.slice(0, desiredCount) };
};

/** "Dublin, Ireland" → "Ireland" (last comma segment); country names pass through. */
const broadenPlaceToCountry = (place: string): string => {
  const parts = place.split(',');
  return (parts[parts.length - 1] ?? place).trim();
};

// ---------------------------------------------------------------------------
// Verified organization stage
//
// Apollo's people search endpoint accessible to our plan returns NO org data
// beyond the name (only has_* flags), so out-of-range companies (10-person
// shop despite a 100–5000 filter, IT firm despite Manufacturing) could never
// be detected. The organizations/search endpoint DOES return the classified
// industry and the real employee count — so when the ICP has firmographic
// criteria we first fetch matching companies, verify them against the
// requested ranges/industries, and then search people ONLY within the
// verified companies. Every preview lead is guaranteed to work at a company
// that demonstrably matches the ICP.
// ---------------------------------------------------------------------------

const MAX_VERIFIED_ORGS = 100;

const runApolloOrgSearch = async (params: URLSearchParams): Promise<ApolloOrgSearchResponse> => {
  const url = `https://api.apollo.io/api/v1/organizations/search?${params.toString()}`;
  console.log('[apollo] POST', url);
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Cache-Control': 'no-cache', 'Content-Type': 'application/json', 'x-api-key': apolloApiKey() },
  });
  const bodyText = await response.text();
  if (!response.ok) {
    console.error('[apollo] org search error', response.status, bodyText);
    throw new Error(apolloErrorMessage('Apollo organization search failed', response.status, bodyText));
  }
  return JSON.parse(bodyText) as ApolloOrgSearchResponse;
};

/** Exported for tests: the org-stage selector is pure and worth pinning directly. */
export const buildOrgSearchParams = (f: Firmographics): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('page', '1');
  params.set('per_page', String(MAX_VERIFIED_ORGS));
  // An explicit company list selects the universe by itself, and nothing else
  // is layered on. A location or industry filter on top could silently drop a
  // firm the operator named by hand, and "I uploaded 40 companies and got leads
  // from 3" is the worst failure this path can produce. The other dimensions
  // still narrow the PEOPLE stage, which is where "which contacts at these
  // firms" actually belongs. `included_organization_keyword_fields[] = name`
  // restricts the fuzzy keyword match to the company NAME (the same mechanism
  // the industry branch below uses, minus the tag/description fields).
  if (f.organizationNames.length > 0) {
    appendList(params, 'q_organization_keyword_tags[]', f.organizationNames);
    appendList(params, 'included_organization_keyword_fields[]', ['name']);
    return params;
  }
  // No explicit company location → constrain the org universe to where the
  // PEOPLE are. People overwhelmingly work at companies in their own country,
  // and without any location the org stage fetches 100 arbitrary companies
  // worldwide, which the people stage then intersects down to near-zero.
  appendList(params, 'organization_locations[]', f.orgLocations.length > 0 ? f.orgLocations : f.personLocations);
  if (f.industryTagIds.length) {
    appendList(params, 'organization_industry_tag_ids[]', f.industryTagIds);
  } else if (f.industryKeywords.length) {
    appendList(params, 'q_organization_keyword_tags[]', f.industryKeywords);
    appendList(params, 'included_organization_keyword_fields[]', ['tags', 'name']);
  }
  appendList(params, 'organization_num_employees_ranges[]', f.employeeRanges);
  if (f.revenueMin != null) params.set('revenue_range[min]', String(f.revenueMin));
  if (f.revenueMax != null) params.set('revenue_range[max]', String(f.revenueMax));
  return params;
};

const parseRange = (range: string): readonly [number, number] | null => {
  const m = range.match(/^(\d+)\s*,\s*(\d+)$/);
  if (!m) return null;
  return [Number(m[1]), Number(m[2])];
};

/**
 * Belt-and-suspenders verification of stage-1 organizations against the
 * requested firmographics using the REAL data Apollo returned. Catches index
 * inconsistencies that Apollo's own filters let through — exactly the outliers
 * the testers keep screenshotting.
 *
 * Exported for tests: pure, and the named-company bypass must not regress
 * silently.
 */
export const verifyOrganization = (org: ApolloOrgSearchOrganization, f: Firmographics): boolean => {
  if (!org.id) return false;
  // Named by the operator → taken at their word. Apollo's own industry
  // classification and headcount are wrong often enough that verifying an
  // explicitly named company against them would quietly discard firms the
  // operator listed, with nothing on screen to say why.
  if (f.organizationNames.length > 0) return true;
  if (f.employeeRanges.length > 0) {
    const employees = org.estimated_num_employees;
    if (typeof employees !== 'number') return false; // unknown size — cannot prove it matches
    const ranges = f.employeeRanges.map(parseRange).filter((r): r is readonly [number, number] => r !== null);
    if (ranges.length > 0 && !ranges.some(([lo, hi]) => employees >= lo && employees <= hi)) return false;
  }
  if (f.industryTagIds.length > 0) {
    if (!org.industry_tag_id || !f.industryTagIds.includes(org.industry_tag_id)) return false;
  }
  return true;
};

interface VerifiedOrg {
  id: string;
  name: string;
  industry?: string;
  employees?: number;
}

/**
 * Fetch + verify companies matching the firmographic criteria. Returns null
 * when the org stage is not applicable (no firmographic criteria) or yielded
 * nothing verifiable (caller falls back to the single-stage people search).
 */
const fetchVerifiedOrgs = async (f: Firmographics): Promise<VerifiedOrg[] | null> => {
  const hasFirmographics =
    f.organizationNames.length > 0 ||
    f.industryTagIds.length > 0 ||
    f.industryKeywords.length > 0 ||
    f.employeeRanges.length > 0;
  if (!hasFirmographics) return null;
  const params = buildOrgSearchParams(f);
  let data = await runApolloOrgSearch(params);
  // Same zero-result fallback as the people search: broaden city-level org
  // locations to their country and retry once.
  if ((data.organizations ?? []).length === 0 && broadenLocationParams(params)) {
    console.log('[apollo] 0 orgs with city-level locations — retrying at country level');
    data = await runApolloOrgSearch(params);
  }
  const orgs = data.organizations ?? [];
  // The org stage can only ever fetch ONE page of MAX_VERIFIED_ORGS companies
  // (organization_ids[] URL length caps a bigger list anyway). When far more
  // companies match — "schools in Germany" → tens of thousands — scoping the
  // people search to 100 arbitrarily-ranked ones collapses the searchable
  // universe and testers saw absurd totals like 1 lead. Precision-verification
  // is only worth it when the org list is (near-)complete, so skip the stage
  // and let Apollo apply the org filters inline in the people search instead.
  const totalOrgs = data.pagination?.total_entries ?? orgs.length;
  // Bailing out drops the org scoping entirely. That is the right trade for a
  // broad firmographic universe (scoping to 100 arbitrary companies collapses
  // the search), but NOT for an explicit company list: there, bailing would
  // throw away the operator's whole constraint and quietly source from anyone.
  if (f.organizationNames.length === 0 && totalOrgs > MAX_VERIFIED_ORGS) {
    console.log('[apollo] org stage skipped — universe exceeds the verifiable cap', { totalOrgs });
    return null;
  }
  const verified: VerifiedOrg[] = [];
  for (const org of orgs) {
    if (!verifyOrganization(org, f)) continue;
    verified.push({
      id: org.id as string,
      name: org.name ?? '',
      industry: org.industry ?? undefined,
      employees: typeof org.estimated_num_employees === 'number' ? org.estimated_num_employees : undefined,
    });
  }
  console.log('[apollo] org stage', { fetched: orgs.length, verified: verified.length });
  return verified.length > 0 ? verified : null;
};

/**
 * Replace city-level location params with their country. Returns true when
 * anything actually changed — used as the guard for the zero-result retry.
 */
const broadenLocationParams = (params: URLSearchParams): boolean => {
  let changed = false;
  for (const key of ['person_locations[]', 'organization_locations[]']) {
    const values = params.getAll(key);
    if (values.length === 0) continue;
    const broadened = dedupeStrings(values.map(broadenPlaceToCountry));
    if (JSON.stringify(broadened) !== JSON.stringify(values)) {
      changed = true;
      params.delete(key);
      appendList(params, key, broadened);
    }
  }
  return changed;
};

export const searchApolloLeadPreviews = async (input: ApolloLeadSearchInput): Promise<ApolloLeadSearchResult> => {
  const forceRemap = readOptionalField(() => input.forceRemap) === true;
  const mapping = await mapIcpToApolloFilters(input, { forceRemap });
  const { params, applied, firmographics } = buildApolloParams(input, mapping);

  // Stage 1 (when firmographic criteria exist): fetch companies via
  // organizations/search — the only accessible endpoint that returns REAL
  // employee counts + classified industries — and verify each against the
  // requested ranges. Stage 2 then searches people ONLY inside the verified
  // companies, so no lead can come from an out-of-range or wrong-industry
  // company. Falls back to the classic single-stage search when the org stage
  // yields nothing (e.g. niche keywords Apollo's org index can't match).
  let verifiedOrgs: VerifiedOrg[] | null = null;
  try {
    verifiedOrgs = await fetchVerifiedOrgs(firmographics);
  } catch (err) {
    console.warn('[apollo] org stage failed — falling back to single-stage people search', err);
  }
  const orgById = new Map<string, VerifiedOrg>();
  const orgByName = new Map<string, VerifiedOrg>();
  if (verifiedOrgs) {
    for (const org of verifiedOrgs) {
      orgById.set(org.id, org);
      if (org.name) orgByName.set(org.name.toLowerCase(), org);
    }
    // Scope the people search to the verified companies. The org-side filters
    // are implied by the id list, so drop them from the people request (also
    // keeps the URL well below length limits).
    for (const key of [
      'organization_locations[]',
      'q_organization_keyword_tags[]',
      'included_organization_keyword_fields[]',
      'organization_industry_tag_ids[]',
      'organization_num_employees_ranges[]',
    ]) {
      params.delete(key);
    }
    params.delete('revenue_range[min]');
    params.delete('revenue_range[max]');
    appendList(params, 'organization_ids[]', [...orgById.keys()]);
  }

  const requestedCount = Math.max(1, input.perPage ?? 5);
  const excludeIds = new Set(readOptionalField(() => input.excludeApolloPersonIds) ?? []);
  // Over-fetch to survive the exclusion filter below — a single-round
  // heuristic (not a full retry loop): ask Apollo for enough extra that a
  // typical exclusion rate still leaves `requestedCount` after filtering,
  // capped so a project with a huge existing lead pool doesn't blow up the
  // request size.
  const desiredCount = excludeIds.size > 0 ? Math.min(requestedCount + excludeIds.size, 500) : requestedCount;
  const startPage = input.page ?? 1;
  let data = await fetchApolloPeople(params, desiredCount, startPage);

  // City-precise searches (e.g. "kitchen studios in Dublin") can hit 0 because
  // Apollo's HQ city differs from the intent. Broaden city-level locations to
  // their country and retry ONCE so the user gets candidates instead of an
  // empty preview — the applied filters reflect the broadened search.
  if ((data.total_entries ?? 0) === 0 && broadenLocationParams(params)) {
    console.log('[apollo] 0 results with city-level locations — retrying at country level');
    data = await fetchApolloPeople(params, desiredCount, startPage);
    applied.personLocations = params.getAll('person_locations[]');
    if (!verifiedOrgs) applied.organizationLocations = params.getAll('organization_locations[]');
  }
  const people = (excludeIds.size > 0 ? (data.people ?? []).filter(p => !p.id || !excludeIds.has(p.id)) : (data.people ?? [])).slice(
    0,
    requestedCount,
  );
  console.log('[apollo] ok', {
    total: data.total_entries,
    fetched: data.people?.length ?? 0,
    excluded: (data.people?.length ?? 0) - people.length,
    returned: people.length,
    verifiedOrgs: verifiedOrgs?.length ?? null,
  });
  return new ApolloLeadSearchResult({
    totalEntries: data.total_entries ?? 0,
    appliedFilters: applied,
    leads: people.map(person => {
      const fullNameParts = (person.name ?? '').split(' ').filter(Boolean);
      // The people endpoint hides org details (only has_* flags) — but for
      // org-scoped searches we already hold the verified company record, so
      // previews can show the REAL industry + employee count.
      const verifiedOrg = person.organization?.name ? orgByName.get(person.organization.name.toLowerCase()) : undefined;
      return new ApolloLeadPreview({
        apolloPersonId: person.id ?? '',
        firstName: person.first_name ?? fullNameParts[0] ?? '',
        lastName: person.last_name ?? person.last_name_obfuscated ?? fullNameParts.slice(1).join(' '),
        email: '',
        company: person.organization?.name ?? '',
        jobTitle: person.title ?? '',
        linkedinUrl: person.linkedin_url ?? undefined,
        industry: person.organization?.industry ?? verifiedOrg?.industry ?? undefined,
        employeeCount: verifiedOrg?.employees,
        lastRefreshedAt: person.last_refreshed_at ?? undefined,
        hasEmail: person.has_email ?? undefined,
        hasDirectPhone: person.has_direct_phone ?? undefined,
      });
    }),
  });
};

export const searchApolloLeads = registerQuery({
  operationName: 'searchApolloLeads',
  type: () => ApolloLeadSearchResult,
  params: [{ name: 'input', type: () => ApolloLeadSearchInput }],
  resolve: (input: ApolloLeadSearchInput) => async (_gqlFields: BaseQLSelectionSet<ApolloLeadSearchResult>) =>
    searchApolloLeadPreviews(input),
});

// EXAMPLE BELOW

// export const askChatAgent = registerMutation({
//   type: () => LLMMessage,
//   operationName: 'askChatAgent',
//   resolve: (conversationID: string, clientTools: LLMToolFunction[]) => {
//     return async (gqlFields: BaseQLSelectionSet<LLMMessage>) => {
//       const conversations = await loadMongoData(LLMConversation, { _id: conversationID }, { sort: { sent: 1 } });
//       const conversation = constructValue(conversations[0]);
//       if (!conversation) throw new Error('Conversation not found');
//       const agentForConversation = getAgent(conversation.agentId);

//       const result = await agentForConversation.run(
//         conversation as LLMConversation,
//         clientTools.map(tool => ({
//           type: 'function',
//           function: { name: tool.name, description: tool.description, parameters: JSON.parse(tool.parameters ?? '{}') },
//         })) as ChatCompletionTool[],
//       );
//       return result;
//     };
//   },
//   params: [
//     { type: () => String, name: 'conversationID' },
//     { type: () => [LLMToolFunction], name: 'clientTools' },
//   ],
// });
